import React, { forwardRef } from "react";
import { EntityCard, EntityCardRef, EntityCardProps, BaseEntity } from "components/Entities/EntityCardBase";
import { Chip } from "ui/Chip";
import { Impact } from "util/constants";
import { CircleCheck } from "lucide-react";
import PostKindChip from "components/Posts/display/PostKindChip";
import PostMediaPreview from "components/Posts/display/PostMediaPreview";
import PostReferenceLink from "components/Posts/display/PostReferenceLink";

interface ImpactCardProps extends Omit<EntityCardProps<Impact>, "entity" | "entityType" | "renderTags" | "renderAdditionalContent" | "renderEditFields" | "renderActions" | "renderPrimaryContent"> {
  impact: Impact;
  onPrivacyChange?: (value: string) => void;
  onCardClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Example of how to use the generic EntityCard component to create ImpactCard
 * This demonstrates how the original ImpactCard can be refactored to use EntityCard
 */
const ImpactCardRefactored = forwardRef<EntityCardRef, ImpactCardProps>(
  (props, ref) => {
    const { impact, onPrivacyChange, onCardClick, ...restProps } = props;
    const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN || "";
    
    // Default card click handler
    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (onCardClick) {
        onCardClick(event);
      }
    };
    
    // Helper function to check if title should be hidden
    const shouldHideTitle = (title?: string): boolean => {
      return !title || title.trim() === '' || title.trim() === '[no_title]';
    };

    // Render impact-specific tags
    const renderTags = (entity: BaseEntity) => {
      const impact = entity as Impact;
      const dimensionInfo = getDimensionInfo(impact);
      
      return (
      <>
        <PostKindChip
          kind="impact"
          style={{
            backgroundColor: getChipColor(impact),
            color: "#fff",
          }}
        />
        {dimensionInfo && (
          <Chip
            label={dimensionInfo.label}
            variant="custom"
            size="sm"
            className="bg-eoe-icon dark:bg-eoe-dark-icon text-eoe-text dark:text-eoe-dark-text"
            icon={<CircleCheck className="size-4" />}
          />
        )}
        {impact.properties?.scope && (
          <Chip
            label={impact.properties.scope}
            variant="custom"
            size="sm"
            style={{
              backgroundColor: "#555555",
              color: "#fff",
            }}
          />
        )}
      </>
      );
    };

    // Render impact-specific additional content
    const renderAdditionalContent = (entity: BaseEntity) => {
      const impact = entity as Impact;
      // Show reference link for non-edit mode
      if (impact.url) {
        return (
          <div className="w-full mb-2">
            <PostReferenceLink
              url={impact.url}
              textClassName="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub"
            />
          </div>
        );
      }
      return null;
    };

    // Render impact-specific edit fields
    const renderEditFields = (entity: BaseEntity) => {
      const impact = entity as Impact;
      // Show reference link (read-only)
      if (impact.url) {
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-eoe-text dark:text-eoe-dark-text mb-1">
              Reference Link
            </label>
            <PostReferenceLink
              url={impact.url}
              textClassName="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub"
            />
          </div>
        );
      }
      return null;
    };

    // Custom render actions for impact thumbnails / link embeds. This card
    // is reserved for non-teabate impacts; teabate posts route through
    // `TeabateCard` (the entity view sets `entity_type = posts.kind`).
    const renderActions = (entity: BaseEntity) => {
      const impact = entity as Impact;

      if (impact.url || impact.properties?.reference_link_img_key || impact.properties?.reference_link_image_url) {
        return (
          <PostMediaPreview
            url={impact.url}
            referenceImageKey={impact.properties?.reference_link_img_key}
            referenceImageUrl={impact.properties?.reference_link_image_url}
            imageCdn={IMAGE_CDN}
            isProfilePage={props.isEntityProfilePage || false}
            onCardClick={handleCardClick}
          />
        );
      }
      return null;
    };

    return (
      <EntityCard
        ref={ref}
        entity={impact}
        entityType="impact"
        onPrivacyChange={onPrivacyChange}
        renderTags={renderTags}
        renderAdditionalContent={renderAdditionalContent}
        renderEditFields={renderEditFields}
        renderActions={renderActions}
        hideTitle={shouldHideTitle(impact.title)}
        descriptionPlaceholder="Body"
        canEditPrivacy={true}
        canEditTitle={false}
        {...restProps}
      />
    );
  }
);

ImpactCardRefactored.displayName = "ImpactCardRefactored";

// Helper functions specific to Impact. (Teabates use `TeabateCard`, so the
// only kind ever passed to this helper is a non-teabate impact.)
const getChipColor = (impact: Impact) => {
  if (impact.rel_type === "impact_positive") return "#419357";
  if (impact.rel_type === "impact_negative") return "#df7023";
  return "#e4a800";
};

// Helper function to get dimension display info
const getDimensionInfo = (impact: Impact): { label: string; key: string } | null => {
  if (!impact.properties?.dimension || impact.properties.dimension === 'none') {
    return null;
  }
  
  // The dimension is now stored directly as the label (e.g., "nationalism", "cosmopolitanism")
  const dimension = impact.properties.dimension;
  
  // Capitalize first letter for display
  const label = dimension.charAt(0).toUpperCase() + dimension.slice(1);
  
  return { label, key: dimension };
};

export default ImpactCardRefactored;